import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import Toggle from '../components/Toggle';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination, { CARD_PAGE_SIZE, usePagedRows } from '../components/Pagination';
import { useToast } from '../components/Toast';
import {
  LegalService,
  LegalServiceInput,
  PricingType,
  ServiceTier,
  createLegalService,
  deleteLegalService,
  extractApiError,
  listLegalServices,
  servicePriceLabel,
  toggleLegalService,
  updateLegalService,
} from '../api/legalServices';

type ModalMode =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; service: LegalService };

/** Editable tier row — prices kept as strings while typing. */
interface TierRow {
  name: string;
  description: string;
  price: string;
}

interface FormState {
  name: string;
  description: string;
  price: string;
  pricingType: PricingType;
  estimatedTurnaround: string;
  tiers: TierRow[];
  isActive: boolean;
}

const EMPTY_TIER: TierRow = { name: '', description: '', price: '' };

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  price: '',
  pricingType: 'flat',
  estimatedTurnaround: '',
  tiers: [],
  isActive: true,
};

const fromService = (s: LegalService): FormState => ({
  name: s.name ?? '',
  description: s.description ?? '',
  price: s.price === null || s.price === undefined ? '' : String(s.price),
  pricingType: s.pricingType ?? 'flat',
  estimatedTurnaround: s.estimatedTurnaround ?? '',
  tiers: Array.isArray(s.tiers)
    ? s.tiers.map((t) => ({
        name: t.name ?? '',
        description: t.description ?? '',
        price:
          t.price === null || t.price === undefined ? '' : String(t.price),
      }))
    : [],
  isActive: s.isActive,
});

const toPayload = (f: FormState): LegalServiceInput => {
  const cleanTiers: ServiceTier[] = f.tiers
    .filter((t) => t.name.trim() !== '')
    .map((t) => ({
      name: t.name.trim(),
      description: t.description.trim() || undefined,
      price:
        t.price.trim() === ''
          ? null
          : Number(t.price.replace(/[^0-9.\-]/g, '')),
    }));

  return {
    name: f.name.trim(),
    description: f.description.trim() || undefined,
    price:
      f.price.trim() === ''
        ? null
        : Number(f.price.replace(/[^0-9.\-]/g, '')),
    pricingType: f.pricingType,
    estimatedTurnaround: f.estimatedTurnaround.trim() || undefined,
    // Tiers only make sense for tiered pricing; null clears them otherwise.
    tiers: f.pricingType === 'tiered' ? cleanTiers : null,
    isActive: f.isActive,
  };
};

// Cards are a fixed size sized to the tallest card's content (name, two
// description lines, turnaround, tier summary). Anything longer is cut here
// and shown in full on the service's detail page.
const CARD_HEIGHT = 215;
const DESC_LIMIT = 80;

/** Cut at the last word boundary before `limit` characters. */
const truncate = (text: string, limit: number): string => {
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const space = cut.lastIndexOf(' ');
  return (space > limit * 0.6 ? cut.slice(0, space) : cut).replace(/[\s.,;:!-]+$/, '');
};

const Legal: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [services, setServices] = useState<LegalService[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalMode>({ kind: 'closed' });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState<boolean>(false);
  const [busyToggleId, setBusyToggleId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LegalService | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { services: list } = await listLegalServices();
      setServices(list);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load legal services'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The detail page's Edit button links here with ?edit=<id>.
  const editId = params.get('edit');
  useEffect(() => {
    if (!editId || loading) return;
    const s = services.find((x) => String(x.id) === editId);
    if (s) {
      setForm(fromService(s));
      setModal({ kind: 'edit', service: s });
    }
    setParams({}, { replace: true });
  }, [editId, loading, services, setParams]);

  const activeCount = useMemo(
    () => services.filter((s) => s.isActive).length,
    [services]
  );

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setModal({ kind: 'add' });
  };

  const openEdit = (s: LegalService) => {
    setForm(fromService(s));
    setModal({ kind: 'edit', service: s });
  };

  const closeModal = () => {
    if (saving) return;
    setModal({ kind: 'closed' });
  };

  const setField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => setForm((f) => ({ ...f, [key]: value }));

  const setTierField = (
    index: number,
    key: keyof TierRow,
    value: string
  ) =>
    setForm((f) => ({
      ...f,
      tiers: f.tiers.map((t, i) => (i === index ? { ...t, [key]: value } : t)),
    }));

  const addTier = () =>
    setForm((f) => ({ ...f, tiers: [...f.tiers, { ...EMPTY_TIER }] }));

  const removeTier = (index: number) =>
    setForm((f) => ({ ...f, tiers: f.tiers.filter((_, i) => i !== index) }));

  const handleToggle = async (s: LegalService) => {
    setBusyToggleId(s.id);
    setServices((prev) =>
      prev.map((x) => (x.id === s.id ? { ...x, isActive: !x.isActive } : x))
    );
    try {
      const updated = await toggleLegalService(s.id);
      setServices((prev) =>
        prev.map((x) => (x.id === s.id ? updated : x))
      );
      toast(
        updated.name + ' ' + (updated.isActive ? 'activated' : 'deactivated'),
        updated.isActive ? 'success' : 'info'
      );
    } catch (err) {
      setServices((prev) =>
        prev.map((x) => (x.id === s.id ? { ...x, isActive: s.isActive } : x))
      );
      toast(extractApiError(err, 'Failed to toggle service'), 'error');
    } finally {
      setBusyToggleId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast('Service name is required', 'error');
      return;
    }
    if (
      form.pricingType === 'tiered' &&
      !form.tiers.some((t) => t.name.trim() !== '')
    ) {
      toast('Add at least one pricing tier (with a name)', 'error');
      return;
    }
    setSaving(true);
    try {
      if (modal.kind === 'add') {
        const created = await createLegalService(toPayload(form));
        setServices((prev) => [created, ...prev]);
        toast(created.name + ' created', 'success');
      } else if (modal.kind === 'edit') {
        const updated = await updateLegalService(
          modal.service.id,
          toPayload(form)
        );
        setServices((prev) =>
          prev.map((x) => (x.id === updated.id ? updated : x))
        );
        toast(updated.name + ' updated', 'success');
      }
      setModal({ kind: 'closed' });
    } catch (err) {
      toast(extractApiError(err, 'Save failed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Stage a delete - opens the themed confirm dialog. */
  const requestDelete = (s: LegalService) => setPendingDelete(s);

  /** Actually perform the delete after user confirms. */
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const s = pendingDelete;
    setDeleting(true);
    try {
      await deleteLegalService(s.id);
      setServices((prev) => prev.filter((x) => x.id !== s.id));
      toast(s.name + ' deleted', 'info');
      setPendingDelete(null);
      setModal({ kind: 'closed' });
    } catch (err) {
      toast(extractApiError(err, 'Delete failed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const modalTitle =
    modal.kind === 'add'
      ? 'Add New Legal Service'
      : modal.kind === 'edit'
      ? 'Edit: ' + modal.service.name
      : '';

  // Cards: 12 per page (four rows of three).
  const { page, setPage, pageRows, total } = usePagedRows(services, CARD_PAGE_SIZE);

  return (
    <div>
      <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
        <div className="text-sm text-g500">
          {loading
            ? 'Loading services...'
            : activeCount + ' of ' + services.length + ' services active'}
        </div>
        <button
          className="btn btn-primary"
          onClick={openAdd}
          disabled={loading || !!loadError}
        >
          <Icon name="plus" size={16} color="#fff" /> Add New Service
        </button>
      </div>

      {loadError && (
        <div
          className="card p-5 mb-5"
          role="alert"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load services</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {loading && !loadError && (
        <div className="card p-12 text-center text-g500">
          <Icon name="package" size={28} color="var(--g400)" /> Loading...
        </div>
      )}

      {!loading && !loadError && services.length === 0 && (
        <div className="card p-12 flex flex-col items-center text-center">
          <div
            className="rounded-[20px] flex items-center justify-center mb-5"
            style={{ width: 72, height: 72, background: 'var(--g100)' }}
          >
            <Icon name="scale" size={32} color="var(--g400)" />
          </div>
          <div className="text-lg font-semibold text-ink mb-1.5">
            No services yet
          </div>
          <div className="text-sm text-g500 mb-5" style={{ maxWidth: 420 }}>
            Click "Add New Service" to create your first legal service.
          </div>
          <button className="btn btn-primary" onClick={openAdd}>
            <Icon name="plus" size={16} color="#fff" /> Add New Service
          </button>
        </div>
      )}

      {!loading && !loadError && services.length > 0 && (
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          }}
        >
          {pageRows.map((s, i) => {
            const desc = s.description || '';
            const shortDesc = truncate(desc, DESC_LIMIT);
            const tiers =
              s.pricingType === 'tiered' && Array.isArray(s.tiers) ? s.tiers : [];
            const price = servicePriceLabel(s);
            const openDetail = () => navigate(`/admin/legal/${s.id}`);
            // The whole card opens the details; its own buttons (name, more,
            // toggle, Edit, Delete) keep their own actions, and selecting text
            // doesn't navigate away.
            const onCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
              if ((e.target as HTMLElement).closest('button, a, input, label')) return;
              if (window.getSelection()?.toString()) return;
              openDetail();
            };
            return (
              <div
                key={s.id}
                className="card flex flex-col px-5 pt-5 pb-4 animate-fadeIn cursor-pointer hover:shadow-md"
                onClick={onCardClick}
                style={{
                  height: CARD_HEIGHT,
                  opacity: s.isActive ? 1 : 0.55,
                  animationDelay: i * 40 + 'ms',
                  transition: 'opacity .2s ease, box-shadow .2s ease',
                }}
              >
                <div className="flex justify-between items-start gap-4 flex-1 min-h-0 overflow-hidden">
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      className="block w-full text-left text-base font-bold mb-1 truncate hover:text-brand"
                      title={s.name}
                      onClick={openDetail}
                    >
                      {s.name}
                    </button>
                    <div
                      className="text-[13px] text-g500 leading-relaxed"
                      style={{ overflowWrap: 'anywhere' }}
                    >
                      {desc ? (
                        <>
                          {shortDesc}
                          {shortDesc.length < desc.length && (
                            <>
                              {'… '}
                              <button
                                type="button"
                                className="font-semibold text-brand hover:underline"
                                onClick={openDetail}
                              >
                                more
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="italic text-g400">No description</span>
                      )}
                    </div>
                    {s.estimatedTurnaround && (
                      <div className="text-[12px] text-g400 mt-2 truncate">
                        Turnaround: {s.estimatedTurnaround}
                      </div>
                    )}
                    {tiers.length > 0 && (
                      <button
                        type="button"
                        className="block text-left text-[12px] text-g500 mt-2 truncate max-w-full hover:text-brand"
                        onClick={openDetail}
                        title="View pricing tiers"
                      >
                        {tiers.length} pricing tier{tiers.length > 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                  <Toggle
                    on={s.isActive}
                    onChange={() => {
                      if (busyToggleId !== s.id) handleToggle(s);
                    }}
                    ariaLabel={'Toggle ' + s.name}
                  />
                </div>
                <div
                  className="flex items-center gap-1.5 pt-3 mt-3"
                  style={{ borderTop: '1px solid var(--g100)' }}
                >
                  {/* Tiered services show their lowest – highest tier price here. */}
                  {price && <span className="text-lg font-bold text-brand">{price}</span>}
                  <button
                    className="btn btn-secondary btn-sm ml-auto"
                    onClick={() => openEdit(s)}
                  >
                    <Icon name="pencil" size={14} /> Edit
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ color: 'var(--red)' }}
                    onClick={() => requestDelete(s)}
                  >
                    <Icon name="x-circle" size={14} color="var(--red)" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && !loadError && (
        <Pagination page={page} pageSize={CARD_PAGE_SIZE} total={total} onChange={setPage} />
      )}

      {/* Add/Edit modal */}
      <Modal
        open={modal.kind !== 'closed'}
        title={modalTitle}
        onClose={closeModal}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="form-label">Service Name</label>
            <input
              className="form-input"
              placeholder="e.g. Title Search"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              autoFocus
              disabled={saving}
            />
          </div>

          <div className="mb-4">
            <label className="form-label">Description</label>
            <textarea
              rows={5}
              className="form-input"
              placeholder="Describe the service..."
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="mb-4">
              <label className="form-label">Price ($)</label>
              <input
                className="form-input"
                type="number"
                step="0.01"
                placeholder="150"
                value={form.price}
                onChange={(e) => setField('price', e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="mb-4">
              <label className="form-label">Pricing Type</label>
              <select
                className="form-input"
                value={form.pricingType}
                onChange={(e) => {
                  const next = e.target.value as PricingType;
                  setForm((f) => ({
                    ...f,
                    pricingType: next,
                    // Seed one empty row so the editor isn't blank.
                    tiers:
                      next === 'tiered' && f.tiers.length === 0
                        ? [{ ...EMPTY_TIER }]
                        : f.tiers,
                  }));
                }}
                disabled={saving}
              >
                <option value="flat">Flat</option>
                <option value="tiered">Tiered</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          {/* Tier editor — only for tiered pricing */}
          {form.pricingType === 'tiered' && (
            <div className="mb-4">
              <label className="form-label">Pricing Tiers</label>
              <div className="flex flex-col gap-2.5">
                {form.tiers.map((t, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-lg"
                    style={{
                      border: '1px solid var(--g200)',
                      background: 'var(--g50, #FAFAFA)',
                    }}
                  >
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <input
                        className="form-input"
                        placeholder={'Tier name, e.g. Phone Consultation'}
                        value={t.name}
                        onChange={(e) => setTierField(i, 'name', e.target.value)}
                        disabled={saving}
                      />
                      <input
                        className="form-input"
                        type="number"
                        step="0.01"
                        placeholder="Price, e.g. 25"
                        value={t.price}
                        onChange={(e) => setTierField(i, 'price', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="flex gap-3 items-center">
                      <input
                        className="form-input"
                        style={{ flex: 1 }}
                        placeholder="Short description, e.g. 30-minute phone call"
                        value={t.description}
                        onChange={(e) =>
                          setTierField(i, 'description', e.target.value)
                        }
                        disabled={saving}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--red)' }}
                        onClick={() => removeTier(i)}
                        disabled={saving}
                        aria-label={'Remove tier ' + (i + 1)}
                      >
                        <Icon name="x-circle" size={16} color="var(--red)" />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={addTier}
                  disabled={saving}
                >
                  <Icon name="plus" size={14} /> Add Tier
                </button>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="form-label">Estimated Turnaround</label>
            <input
              className="form-input"
              placeholder="e.g. 2-3 business days"
              value={form.estimatedTurnaround}
              onChange={(e) => setField('estimatedTurnaround', e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="mb-2">
            <label className="form-label">Status</label>
            <div className="flex items-center gap-2.5 py-1.5">
              <Toggle
                on={form.isActive}
                onChange={() => setField('isActive', !form.isActive)}
              />
              <span className="text-[13px] text-g500">
                {form.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div
            className="flex gap-2.5 justify-end mt-6"
            style={{ flexWrap: 'wrap' }}
          >
            {modal.kind === 'edit' && (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: 'var(--red)', marginRight: 'auto' }}
                onClick={() => modal.kind === 'edit' && requestDelete(modal.service)}
                disabled={saving}
              >
                <Icon name="x-circle" size={14} color="var(--red)" /> Delete
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving
                ? 'Saving...'
                : modal.kind === 'add'
                ? 'Create Service'
                : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Themed delete confirmation */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete Service"
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Keep"
        busy={deleting}
        message={
          <>
            Are you sure you want to delete{' '}
            <strong style={{ color: 'var(--ink)' }}>
              {pendingDelete?.name}
            </strong>
            ?
            <br />
            This cannot be undone.
          </>
        }
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
};

export default Legal;
